import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('nb-NO');
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

function formatShortDate(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}`;
}

@observer
class ArticleStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            isLoading: true,
            error: null,
            // Default to 30 days: 'all' scans every scrollView session for the article
            // (140k+ on popular articles) and is too slow as a first load.
            period: '30d', // 'today', '7d', '30d', 'all', 'custom'
            customStartDate: '',
            customEndDate: '',
        };
    }

    loadStats = async (periodOverride) => {
        const { articleId } = this.props;
        const { period, customStartDate, customEndDate } = this.state;
        const activePeriod = periodOverride || period;

        if (!articleId) {
            this.setState({ error: 'No article ID provided', isLoading: false });
            return;
        }

        this.setState({ isLoading: true });

        try {
            let url = `/api/articles/${articleId}/stats?period=${activePeriod}`;
            if (activePeriod === 'custom' && customStartDate && customEndDate) {
                url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
            }
            const response = await util.fetchApi(url, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ stats: response.data, isLoading: false });
            } else {
                this.setState({ error: response.message || 'Failed to load stats', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    markFeedbackReviewed = async (id) => {
        const { articleFeedbackStore } = this.props.stores;
        if (!articleFeedbackStore) return;
        const response = await articleFeedbackStore.setStatus(id, 'reviewed');
        if (response?.status === 202) {
            // Reflect the change locally without a full stats reload.
            this.setState(prev => ({
                stats: {
                    ...prev.stats,
                    feedback: {
                        ...prev.stats.feedback,
                        comments: (prev.stats.feedback?.comments || []).map(c => (c.id === id ? { ...c, status: 'reviewed' } : c)),
                    },
                },
            }));
        }
    }

    renderFeedbackComments(comments) {
        return (
            <div class='list-group'>
                {comments.map(c => (
                    <div key={c.id} class='list-group-item d-flex flex-column gap-1'>
                        <div class='d-flex align-items-center gap-2'>
                            <i class={`fa-solid ${c.vote === 'up' ? 'fa-thumbs-up text-success' : 'fa-thumbs-down text-danger'}`} />
                            <small class='text-body-secondary'>{formatShortDate(String(c.createdDate || '').substring(0, 10))} · user {c.user}</small>
                            {c.status === 'reviewed' ? (
                                <span class='badge text-bg-secondary ms-auto'>Reviewed</span>
                            ) : (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill ms-auto text-nowrap'
                                    onClick={() => this.markFeedbackReviewed(c.id)}
                                >
                                    <i class='fa-solid fa-check' /> Mark as reviewed
                                </button>
                            )}
                        </div>
                        <div style='white-space: pre-wrap;'>{c.comment}</div>
                    </div>
                ))}
            </div>
        );
    }

    handlePeriodChange = (newPeriod) => {
        this.setState({ period: newPeriod }, () => {
            if (newPeriod !== 'custom') {
                this.loadStats(newPeriod);
            }
        });
    }

    handleDateChange = (field, value) => {
        this.setState({ [field]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        if (customStartDate && customEndDate) {
            this.loadStats('custom');
        }
    }

    componentDidMount() {
        this.loadStats();
    }

    renderTrend(trend) {
        if (!trend || trend === 0) return null;
        const isPositive = trend > 0;
        return (
            <span class={`ms-1 ${isPositive ? 'text-success' : 'text-danger'}`}>
                {isPositive ? '↑' : '↓'}{Math.abs(trend)}%
            </span>
        );
    }

    renderSummaryCard(title, value, icon, trend, darkmode, subtitle) {
        return (
            <div class='col-6 col-md-3 mb-3'>
                <div class={`rounded-lg p-3 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='text-muted small mb-1'>
                        <i class={`fa-solid ${icon} me-1`} />{title}
                    </div>
                    <div class='d-flex align-items-baseline'>
                        <span class='h4 mb-0 font-weight-bold'>{value}</span>
                        {this.renderTrend(trend)}
                    </div>
                    {subtitle && <div class='text-muted small mt-1'>{subtitle}</div>}
                </div>
            </div>
        );
    }

    renderEngagement(engagement) {
        const views = engagement?.combinedViews || 0;

        if (views === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No views in this period yet
                </div>
            );
        }

        const items = [
            { label: 'Views', value: views, color: 'bg-primary', icon: 'fa-eye' },
            { label: 'Video plays', value: engagement?.videoPlays || 0, color: 'bg-warning', icon: 'fa-play' },
            { label: 'Link clicks', value: engagement?.linkClicks || 0, color: 'bg-success', icon: 'fa-link' },
            { label: 'Carousel scrolls', value: engagement?.carouselScrolls || 0, color: 'bg-info', icon: 'fa-images' },
        ];

        return (
            <div>
                {items.map(item => {
                    const percent = views > 0 ? Math.round((item.value / views) * 100) : 0;
                    const barWidth = Math.min(Math.max(percent, item.value > 0 ? 2 : 0), 100);
                    return (
                        <div class='mb-2' key={item.label}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span><i class={`fa-solid ${item.icon} me-1`} />{item.label}</span>
                                <span class='text-muted'>{formatNumber(item.value)} ({percent}%)</span>
                            </div>
                            <div class='progress' style='height: 14px;'>
                                <div
                                    class={`progress-bar ${item.color}`}
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    getCountryFlag(countryCode) {
        if (!countryCode || countryCode.length !== 2) return '';
        // Convert country code to flag emoji (regional indicator symbols)
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    renderVisitorMap(cityBreakdown) {
        const cities = (cityBreakdown || []).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon));
        if (cities.length === 0) {
            return null;
        }
        const maxVisitors = Math.max(...cities.map(c => c.visitors));
        const bounds = cities.map(c => [c.lat, c.lon]);

        return (
            <div class='rounded-lg overflow-hidden' style='height: 300px;'>
                <MapContainer
                    bounds={bounds}
                    boundsOptions={{ padding: [40, 40], maxZoom: 10 }}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    {cities.map(c => (
                        <CircleMarker
                            key={`${c.city}-${c.countryCode}`}
                            center={[c.lat, c.lon]}
                            radius={6 + Math.round(14 * Math.sqrt(c.visitors / maxVisitors))}
                            pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.4 }}
                        >
                            <Popup>
                                <strong>{c.city}</strong>{c.country ? `, ${c.country}` : ''}<br />
                                {formatNumber(c.visitors)} {c.visitors === 1 ? 'visitor' : 'visitors'}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        );
    }

    renderCountryBreakdown(countryBreakdown) {
        if (!countryBreakdown || countryBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...countryBreakdown.map(c => c.visitors));

        return (
            <div>
                {countryBreakdown.map((country, idx) => {
                    const barWidth = maxVisitors > 0 ? (country.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={country.countryCode || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {country.countryCode && <span class='me-2'>{this.getCountryFlag(country.countryCode)}</span>}
                                    {country.country}
                                </span>
                                <span class='text-muted'>{formatNumber(country.visitors)} ({country.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-primary'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCityBreakdown(cityBreakdown) {
        if (!cityBreakdown || cityBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...cityBreakdown.map(c => c.visitors));

        return (
            <div>
                {cityBreakdown.map((city, idx) => {
                    const barWidth = maxVisitors > 0 ? (city.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={`${city.city}-${city.countryCode}` || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {city.countryCode && <span class='me-2'>{this.getCountryFlag(city.countryCode)}</span>}
                                    {city.city}{city.country ? ` (${city.country})` : ''}
                                </span>
                                <span class='text-muted'>{formatNumber(city.visitors)} ({city.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-info'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderDailyChart(dailyStats) {
        if (!dailyStats || dailyStats.length === 0) {
            return (
                <div class='text-muted text-center py-4'>
                    No daily data available yet
                </div>
            );
        }

        const dayTotal = d => (d.views || 0) + (d.scrollViews || 0) + (d.videoPlays || 0) + (d.linkClicks || 0);
        const maxTotal = Math.max(...dailyStats.map(dayTotal));
        const displayData = dailyStats.slice(-30);
        const barWidth = Math.floor(100 / Math.min(displayData.length, 30));

        return (
            <div class='d-flex align-items-end justify-content-between' style='height: 130px; overflow-x: auto;'>
                {displayData.map((day, idx) => {
                    const total = dayTotal(day);
                    const viewsHeight = maxTotal > 0 ? ((day.views || 0) / maxTotal) * 100 : 0;
                    const scrollHeight = maxTotal > 0 ? ((day.scrollViews || 0) / maxTotal) * 100 : 0;
                    const videoHeight = maxTotal > 0 ? ((day.videoPlays || 0) / maxTotal) * 100 : 0;
                    const linkHeight = maxTotal > 0 ? ((day.linkClicks || 0) / maxTotal) * 100 : 0;
                    const dateLabel = day.date ? day.date.split('-')[2] : idx + 1;

                    return (
                        <div
                            key={idx}
                            class='d-flex flex-column align-items-center flex-shrink-0'
                            style={`width: ${barWidth}%; min-width: 12px; max-width: 24px;`}
                            title={`${day.date}: ${formatNumber(total)} total`}
                        >
                            <div class='d-flex flex-column-reverse' style='height: 100px; width: 100%;'>
                                <div class='bg-primary' style={`height: ${viewsHeight}%; min-height: ${day.views > 0 ? '2px' : '0'};`} />
                                <div class='bg-info' style={`height: ${scrollHeight}%; min-height: ${day.scrollViews > 0 ? '2px' : '0'};`} />
                                <div class='bg-warning' style={`height: ${videoHeight}%; min-height: ${day.videoPlays > 0 ? '2px' : '0'};`} />
                                <div class='bg-success' style={`height: ${linkHeight}%; min-height: ${day.linkClicks > 0 ? '2px' : '0'};`} />
                            </div>
                            <div class='text-muted small mt-1' style='font-size: 0.55rem;'>
                                {dateLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeakHours(peakHours) {
        if (!peakHours || peakHours.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No peak hours data available
                </div>
            );
        }

        const maxViews = Math.max(...peakHours.map(p => p.views || 0));
        const chartHeight = 80;

        return (
            <div>
                <div class='d-flex align-items-end justify-content-between' style={`height: ${chartHeight}px;`}>
                    {peakHours.map((hour, idx) => {
                        const barHeight = maxViews > 0
                            ? Math.max(((hour.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isPeak = hour.views === maxViews && maxViews > 0;

                        return (
                            <div
                                key={idx}
                                class='d-flex flex-column justify-content-end'
                                style={`width: 4%; min-width: 8px; height: ${chartHeight}px;`}
                                title={`${hour.hour}:00 - ${formatNumber(hour.views)} views`}
                            >
                                <div
                                    class={isPeak ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-between text-muted small mt-1'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>
            </div>
        );
    }

    renderWeekdays(weekdayStats) {
        if (!weekdayStats || weekdayStats.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No weekday data available
                </div>
            );
        }

        const maxViews = Math.max(...weekdayStats.map(w => w.views || 0));
        const chartHeight = 80;
        const best = weekdayStats.reduce((max, w) => (w.views > max.views ? w : max), weekdayStats[0]);

        return (
            <div>
                <div class='d-flex align-items-end justify-content-around' style={`height: ${chartHeight}px;`}>
                    {weekdayStats.map(day => {
                        const barHeight = maxViews > 0
                            ? Math.max(((day.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isBest = maxViews > 0 && day.views === maxViews;

                        return (
                            <div
                                key={day.isoWeekday}
                                class='d-flex flex-column justify-content-end align-items-center'
                                style={`width: 10%; height: ${chartHeight}px;`}
                                title={`${WEEKDAY_LABELS[day.isoWeekday - 1]}: ${formatNumber(day.views)} views`}
                            >
                                <div
                                    class={isBest ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; max-width: 40px; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-around text-muted small mt-1'>
                    {weekdayStats.map(day => (
                        <span key={day.isoWeekday} style='width: 10%; text-align: center;'>
                            {WEEKDAY_LABELS[day.isoWeekday - 1]}
                        </span>
                    ))}
                </div>
                {maxViews > 0 && (
                    <div class='text-muted small mt-2'>
                        <i class='fa-solid fa-lightbulb me-1' />
                        Most traffic on {WEEKDAY_LABELS[best.isoWeekday - 1]} — a good day to post.
                    </div>
                )}
            </div>
        );
    }

    renderWeeklyHistory(weeklyStats, darkmode) {
        if (!weeklyStats || weeklyStats.length < 2) {
            return null;
        }

        const displayData = weeklyStats.slice(-12);
        const maxTotal = Math.max(...displayData.map(w => (w.views || 0) + (w.scrollViews || 0)));
        const chartHeight = 80;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-calendar-week me-2' />
                    Weekly History (Last 12 Weeks)
                </h6>
                <div class='d-flex align-items-end justify-content-around' style={`height: ${chartHeight}px;`}>
                    {displayData.map(week => {
                        const viewsHeight = maxTotal > 0 ? ((week.views || 0) / maxTotal) * chartHeight : 0;
                        const scrollHeight = maxTotal > 0 ? ((week.scrollViews || 0) / maxTotal) * chartHeight : 0;
                        return (
                            <div
                                key={week.week}
                                class='d-flex flex-column justify-content-end align-items-center'
                                style={`width: 7%; height: ${chartHeight}px;`}
                                title={`Week ${week.week}: ${formatNumber(week.views)} views, ${formatNumber(week.scrollViews)} scroll views`}
                            >
                                <div class='d-flex flex-column-reverse' style='width: 100%; max-width: 28px;'>
                                    <div class='bg-primary' style={`height: ${Math.max(viewsHeight, week.views > 0 ? 2 : 0)}px; border-radius: 2px 2px 0 0;`} />
                                    <div class='bg-info' style={`height: ${Math.max(scrollHeight, week.scrollViews > 0 ? 2 : 0)}px;`} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-around text-muted small mt-1'>
                    {displayData.map(week => (
                        <span key={week.week} style='width: 7%; text-align: center; font-size: 0.6rem;'>
                            {String(week.week).replace(/^\d{4}-?W?/, '')}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    renderPeriodSelector(darkmode) {
        const { period, customStartDate, customEndDate, isLoading } = this.state;

        const periods = [
            { key: 'today', label: 'Today' },
            { key: '7d', label: '7 days' },
            { key: '30d', label: '30 days' },
            { key: 'all', label: 'All time' },
            { key: 'custom', label: 'Custom' },
        ];

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <span class='text-muted small me-2'>
                        <i class='fa-solid fa-filter me-1' />
                        Period:
                    </span>
                    <div class='btn-group btn-group-sm'>
                        {periods.map(p => (
                            <button
                                key={p.key}
                                type='button'
                                class={`btn ${period === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => this.handlePeriodChange(p.key)}
                                disabled={isLoading}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {period === 'custom' && (
                        <div class='d-flex align-items-center gap-2 ms-2'>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customStartDate}
                                onChange={(e) => this.handleDateChange('customStartDate', e.target.value)}
                            />
                            <span class='text-muted'>to</span>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customEndDate}
                                onChange={(e) => this.handleDateChange('customEndDate', e.target.value)}
                            />
                            <button
                                type='button'
                                class='btn btn-sm btn-primary'
                                onClick={this.handleCustomDateSubmit}
                                disabled={!customStartDate || !customEndDate || isLoading}
                            >
                                <i class='fa-solid fa-search' />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderSponsorStats = (sponsors) => {
        return (
            <table class='table table-sm mb-0 align-middle'>
                <thead>
                    <tr>
                        <th colSpan='2'>Sponsor</th>
                        <th class='text-end'>Views</th>
                        <th class='text-end'>Clicks</th>
                        <th class='text-end'>CTR</th>
                    </tr>
                </thead>
                <tbody>
                    {sponsors.map(sponsor => (
                        <tr key={sponsor.md5}>
                            <td style='width: 60px;'>
                                {sponsor.imageUrl && <img src={sponsor.imageUrl} alt={sponsor.name} class='img-fluid' style='max-height: 28px;' />}
                            </td>
                            <td>
                                {sponsor.name || <span class='text-body-secondary fst-italic'>(no name)</span>}
                                {sponsor.url && <small class='d-block text-body-secondary text-truncate' style='max-width: 220px;'>{sponsor.url}</small>}
                            </td>
                            <td class='text-end'>{formatNumber(sponsor.views)}</td>
                            <td class='text-end'>{formatNumber(sponsor.clicks)}</td>
                            <td class='text-end'>{sponsor.views > 0 ? `${sponsor.ctr}%` : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    render() {
        const { stats, isLoading, error, period } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !stats) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!stats) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No statistics available</div>
                </div>
            );
        }

        const {
            article, summary, engagement, dailyStats, peakHours, weekdayStats, weeklyStats,
            deviceBreakdown, countryBreakdown, cityBreakdown,
        } = stats;
        const hasSessions = summary?.totalSessions > 0;
        const visitorMap = this.renderVisitorMap(cityBreakdown);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='mb-4'>
                        <h5 class='mb-1'>
                            <i class='fa-solid fa-chart-line me-2' />
                            Statistics: {article?.title}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        {article?.category && (
                            <div class='text-muted small'>
                                <i class='fa-solid fa-tag me-1' />
                                {article.category}
                            </div>
                        )}
                    </div>

                    {/* Period Selector */}
                    {this.renderPeriodSelector(darkmode)}

                    {/* Summary Cards */}
                    <div class='row'>
                        {this.renderSummaryCard('Total Views', formatNumber(summary?.combinedViews), 'fa-eye', period === 'all' ? summary?.viewsTrend : null, darkmode,
                            `${formatNumber(summary?.totalViews)} page + ${formatNumber(summary?.totalScrollViews)} scroll`)}
                        {this.renderSummaryCard('Unique Visitors', formatNumber(summary?.uniqueVisitors), 'fa-users', null, darkmode)}
                        {this.renderSummaryCard('Avg Read Time', summary?.avgReadTime > 0 ? formatDuration(summary.avgReadTime) : '—', 'fa-book-open', null, darkmode, 'all time')}
                        {this.renderSummaryCard('Avg Session', hasSessions ? formatDuration(summary?.avgSessionDuration) : '—', 'fa-stopwatch', null, darkmode,
                            hasSessions ? `${formatNumber(summary.totalSessions)} sessions` : null)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Video Plays', formatNumber(summary?.videoPlays), 'fa-play', null, darkmode)}
                        {this.renderSummaryCard('Link Clicks', formatNumber(summary?.linkClicks), 'fa-link', null, darkmode)}
                        {this.renderSummaryCard('Logged-in Users', formatNumber(summary?.loggedInUsers), 'fa-user-check', null, darkmode)}
                        {this.renderSummaryCard('Mobile', hasSessions ? `${deviceBreakdown?.mobile || 0}%` : '—', 'fa-mobile-screen', null, darkmode)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Views/User', summary?.viewsPerUser || '0', 'fa-chart-line', null, darkmode)}
                        {this.renderSummaryCard('Peak Hour', `${summary?.peakHour || 0}:00`, 'fa-clock', null, darkmode)}
                        {this.renderSummaryCard('Best Day', summary?.bestDay ? formatShortDate(summary.bestDay.date) : '—', 'fa-star', null, darkmode,
                            summary?.bestDay ? `${formatNumber(summary.bestDay.combined)} views` : null)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Helpful', formatNumber(summary?.feedbackUp), 'fa-thumbs-up', null, darkmode)}
                        {this.renderSummaryCard('Not helpful', formatNumber(summary?.feedbackDown), 'fa-thumbs-down', null, darkmode)}
                    </div>

                    {/* Engagement */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-hand-pointer me-2' />
                            Engagement
                        </h6>
                        {this.renderEngagement(engagement)}
                    </div>

                    {/* Reader feedback comments («Hva manglet?») */}
                    {stats.feedback?.comments?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-comment-dots me-2' />
                                Reader feedback
                            </h6>
                            {this.renderFeedbackComments(stats.feedback.comments)}
                        </div>
                    )}

                    {/* Sponsors (hero bar logos): views / clicks / CTR per logo */}
                    {stats.sponsors?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-handshake me-2' />
                                Sponsors
                            </h6>
                            {this.renderSponsorStats(stats.sponsors)}
                        </div>
                    )}

                    {/* Visitors Map */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-map-location-dot me-2' />
                            Visitors Map
                        </h6>
                        {visitorMap || (
                            <div class='text-muted text-center py-4'>
                                <i class='fa-solid fa-map-location-dot d-block mb-2' style='font-size: 1.5rem;' />
                                No visitor locations yet.<br />
                                <span class='small'>
                                    Locations come from reader scroll sessions. Admin visits are not counted.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Country Breakdown */}
                    {countryBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-globe me-2' />
                                Visitors by Country
                            </h6>
                            {this.renderCountryBreakdown(countryBreakdown)}
                        </div>
                    )}

                    {/* City Breakdown */}
                    {cityBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-city me-2' />
                                Visitors by City
                            </h6>
                            {this.renderCityBreakdown(cityBreakdown)}
                        </div>
                    )}

                    {/* Daily Traffic Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3 flex-wrap'>
                            <h6 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Daily Traffic (Last 30 Days)
                            </h6>
                            <div class='d-flex gap-3 small'>
                                <span><span class='badge bg-primary'>&nbsp;</span> Views</span>
                                <span><span class='badge bg-info'>&nbsp;</span> Scroll views</span>
                                <span><span class='badge bg-warning'>&nbsp;</span> Video plays</span>
                                <span><span class='badge bg-success'>&nbsp;</span> Link clicks</span>
                            </div>
                        </div>
                        {this.renderDailyChart(dailyStats)}
                    </div>

                    {/* Peak Hours Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-clock me-2' />
                            Peak Hours (Views by Hour)
                        </h6>
                        {this.renderPeakHours(peakHours)}
                    </div>

                    {/* Best Weekday */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-calendar-days me-2' />
                            Traffic by Weekday
                        </h6>
                        {this.renderWeekdays(weekdayStats)}
                    </div>

                    {/* Weekly History */}
                    {this.renderWeeklyHistory(weeklyStats, darkmode)}

                </div>
            </div>
        );
    }
}

export default ArticleStatsDrawer;
